// src/pages/FinalRankingPage.jsx
// The result of the tournament, made to be shown to everybody at the end: a podium and the full ranking.
import React, { useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { subscribeToAuth } from '../auth';
import AppShell from '../components/AppShell';
import Podium from '../components/ranking/Podium';
import RankingTable from '../components/ranking/RankingTable';
import TeamRankingTable from '../components/ranking/TeamRankingTable';
import { ArrowLeftIcon, TrophyIcon } from '../components/icons';
import { formatDateRange } from '../utils/time';
import ResultsExport from '../components/ResultsExport';
import { useRanking } from './tournament/useRanking';
import { hasResults, ScoreHelp } from './tournament/RankingPanel';

function FinalRankingPage() {
    const user = useContext(UserContext);
    const { tournamentId } = useParams();
    const navigate = useNavigate();
    const byPlayer = useRanking(tournamentId, user);
    const grouped = byPlayer.tournament?.format === 'groups-finals';
    const byTeam = byPlayer.tournament?.format === 'fixed-teams' || grouped;
    const teamRanking = useRanking(tournamentId, user, { byTeam, enabled: byTeam });
    const { standings, tournament, ranking, loading, failed } = byTeam ? teamRanking : byPlayer;

    // Signed out (or the session ended): back to the login page.
    useEffect(() => {
        const unsubscribe = subscribeToAuth((authUser) => {
            if (!authUser) navigate('/');
        });
        return () => unsubscribe?.();
    }, [navigate]);

    const name = tournament?.name || `Tournament ${tournamentId}`;
    const dates = tournament ? formatDateRange(tournament.startDate, tournament.endDate) : '';

    return (
        <AppShell user={user}>
            <div className="page-head">
                <button className="btn btn-ghost btn-sm back-link" onClick={() => navigate(`/tournament/${tournamentId}`)}>
                    <ArrowLeftIcon size={16} /> Back to tournament
                </button>
            </div>

            <div className="final">
                <header className="final-header">
                    <span className="final-trophy" aria-hidden="true">
                        <TrophyIcon size={34} />
                    </span>
                    <p className="final-kicker">Final ranking</p>
                    <h1>{name}</h1>
                    {dates && <p className="muted">{dates}</p>}
                </header>

                {loading ? (
                    <div className="skeleton" aria-busy="true" />
                ) : failed && standings.length === 0 ? (
                    <div className="empty">
                        <strong>The ranking could not be loaded</strong>
                        <p>Please try again in a moment.</p>
                    </div>
                ) : !hasResults(standings) ? (
                    <div className="empty">
                        <strong>No results yet</strong>
                        <p>Enter some scores and the final ranking will show up here.</p>
                    </div>
                ) : byTeam ? (
                    <>
                        <Podium standings={standings} ranking={ranking || undefined}
                               nameOf={(row) => row.players.map((player) => `${player.firstName} ${player.lastName}`).join(' & ')} />
                        <ResultsExport name={name} dates={dates} standings={standings} ranking={ranking} />
                        <div className="card ranking-card">
                            <TeamRankingTable standings={standings} ranking={ranking || undefined} />
                            {grouped && (
                                <p className="hint ranking-note">
                                    Placed by the knockout, then by the group results; the figures are those of the group games.
                                </p>
                            )}
                            <ScoreHelp ranking={ranking} />
                        </div>
                    </>
                ) : (
                    <>
                        <Podium standings={standings} ranking={ranking || undefined} />
                        <ResultsExport name={name} dates={dates} standings={standings} ranking={ranking} />
                        <div className="card ranking-card">
                            <RankingTable standings={standings} ranking={ranking || undefined} />
                            <ScoreHelp ranking={ranking} />
                        </div>
                    </>
                )}
            </div>
        </AppShell>
    );
}

export default FinalRankingPage;
